<template>
  <div class="teams-upgrade-view container py-4">
    <nav aria-label="breadcrumb" class="mb-4" v-if="error || !team">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
            Bereiche
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            :to="{ name: 'teams-detail', params: { id: teamId } }"
            class="text-decoration-none"
          >
            Quittungsverwaltung
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            :to="{ name: 'teams-settings', params: { id: teamId } }"
            class="text-decoration-none"
          >
            Bereich-Einstellungen
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Bereich-Upgrade</li>
      </ol>
    </nav>
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Daten werden geladen...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <div v-else-if="!team" class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Bereich nicht gefunden.
    </div>

    <div v-else>
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <font-awesome-icon icon="crown" class="me-2" />
            Bereichs-Upgrade
          </h2>
          <p class="text-muted mb-0">Upgrade {{ team.name }} auf einen höheren Plan</p>
        </div>
        <BackButton fallback="teams-settings" :params="{ id: teamId }" />
      </div>

 
      <nav aria-label="breadcrumb" class="mb-4">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
              Bereiche
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link
              :to="{ name: 'teams-detail', params: { id: teamId } }"
              class="text-decoration-none"
            >
              Quittungsverwaltung
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link
              :to="{ name: 'teams-settings', params: { id: teamId } }"
              class="text-decoration-none"
            >
              Bereich-Einstellungen
            </router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">Bereichs-Upgrade</li>
        </ol>
      </nav>
      <!-- Current Plan Info -->
      <div class="card border-0 bg-gradient-primary mb-4">
        <div class="card-body py-3">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <font-awesome-icon icon="crown" class="me-2" />
              <span class="white">Aktueller Plan</span>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div>
                <h6 class="mb-0 text-white">{{ getPlanDisplayName(team.status) }}</h6>
                <small class="text-white-50">{{ getPlanDescription(team.status) }}</small>
              </div>
              <div>
                <span :class="getPlanBadgeClass(team.status)" class="badge fs-6">
                  {{ team.status === 'free' ? 'Kostenlos' : team.status.toUpperCase() }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Coupon Section -->
      <div v-if="canUpgrade" class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">
            <font-awesome-icon icon="gift" class="me-2" />
            Gutschein einlösen
          </h5>
        </div>
        <div class="card-body">
          <p class="text-muted mb-3">
            Hast du einen Gutscheincode? Löse ihn hier ein, um ein kostenloses Bereich-Upgrade
            zu erhalten.
          </p>
          <div class="row">
            <div class="col">
              <div class="input-group">
                <input
                  v-model="couponCode"
                  type="text"
                  class="form-control"
                  placeholder="Gutscheincode eingeben"
                  :disabled="couponLoading"
                />
                <button
                  class="btn btn-outline-primary"
                  type="button"
                  :disabled="!couponCode.trim() || couponLoading"
                  @click="validateCoupon"
                >
                  <span
                    v-if="couponLoading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  <font-awesome-icon v-else icon="check" class="me-2" />
                  {{ couponLoading ? 'Prüfe...' : 'Einlösen' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Coupon Result -->
          <div v-if="couponResult" class="mt-3">
            <div v-if="couponResult.success" class="alert alert-success">
              <div class="d-flex align-items-center">
                <font-awesome-icon icon="check-circle" class="me-2" />
                <div>
                  <strong>{{ couponResult.data.coupon.name }}</strong>
                  <div v-if="couponResult.data.description" class="small text-muted mb-1">
                    {{ couponResult.data.description }}
                  </div>
                  <div class="small">{{ getCouponDescription(couponResult.data.coupon) }}</div>
                  <div
                    v-if="hasCouponConditions(couponResult.data.coupon)"
                    class="small text-muted mt-2"
                  >
                    <hr class="my-1" />
                    <div class="fw-bold mb-1">Bonusbedingungen:</div>
                    {{ getCouponConditions(couponResult.data.coupon) }}
                  </div>
                </div>
              </div>
              <div class="mt-2">
                <button class="btn btn-success btn-sm" @click="applyCoupon(couponResult.data)">
                  <font-awesome-icon icon="gift" class="me-2" />
                  Kostenloses Upgrade anwenden
                </button>
              </div>
            </div>
            <div v-else class="alert alert-danger">
              <div class="d-flex align-items-center">
                <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                <span>{{ couponResult.error }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
           <!-- Upgrade Info Alert -->
      <div class="alert alert-warning mb-4" v-if="team.status === 'free'">
        <div class="d-flex align-items-start">
          <font-awesome-icon icon="exclamation-triangle" class="me-3 mt-1 text-dark" />
          <div>
            <h6 class="alert-heading mb-2 text-dark">Wichtiger Hinweis</h6>
            <p class="mb-0 text-dark">
              <strong>Bereich-spezifisches Upgrade:</strong> Das Upgrade gilt ausschließlich für den Bereich "{{ team.name }}". 
              Andere Bereiche in deinem Account erhalten kein Upgrade, dies muss seperat erworben werden.
            </p>
          </div>
        </div>
      </div>

      <div class="row justify-content-center" :class="{ 'justify-content-between': availablePlans.length > 1 }" v-if="availablePlans.length > 0">
        <div v-for="plan in availablePlans" :key="plan.type" class="col-md-6 mb-4">
          <div class="card h-100" :class="getPlanCardClass(plan.type)">
            <div class="card-header text-center">
              <h5 class="mb-0">
                <font-awesome-icon
                  :icon="plan.type === 'team_pro' ? 'star' : 'crown'"
                  class="me-2"
                />
                {{ plan.type === 'team_pro' ? 'Pro Plan' : 'Ultimate Plan' }}
              </h5>
            </div>
            <div class="card-body">
              <div v-if="plan.description" class="mb-3">
                <p class="text-muted">{{ plan.description }}</p>
              </div>

              <!-- Price Info -->
              <div class="text-center mb-4">
                <div class="h3 mb-1">{{ formatCurrency(plan.teamPlanPrice) }}</div>
                <small class="text-muted">pro Jahr</small>
                <div class="mt-2">
                  <span class="badge bg-secondary">
                    {{ plan.teamPlanCoins }} Quittungen pro Monat
                  </span>
                </div>
              </div>

              <!-- Features -->
              <div v-if="plan.teamPlanFeatures && plan.teamPlanFeatures.length > 0" class="mb-4">
                <h6 class="mb-3">Features:</h6>
                <ul class="list-unstyled">
                  <li
                    v-for="feature in plan.teamPlanFeatures"
                    :key="feature"
                    class="mb-2 d-flex align-items-start"
                  >
                    <font-awesome-icon icon="check" class="text-success me-2 mt-1" size="sm" />
                    <span>{{ feature }}</span>
                  </li>
                </ul>
              </div>

              <!-- Action Button -->
              <div class="text-center">
                <button
                  class="btn btn-primary w-100"
                  :disabled="!canUpgrade || team.status === plan.type"
                  @click="upgradeToPlan(plan)"
                >
                  <span v-if="team.status === plan.type">
                    <font-awesome-icon icon="check" class="me-2" />
                    Aktueller Plan
                  </span>
                  <span v-else-if="!canUpgrade">
                    <font-awesome-icon icon="lock" class="me-2" />
                    Upgrade nicht verfügbar
                  </span>
                  <span v-else>
                    <font-awesome-icon icon="arrow-up" class="me-2" />
                    Zu {{ plan.type === 'team_pro' ? 'Pro' : 'Ultimate' }} upgraden
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Plans Available -->
      <div v-if="availablePlans.length === 0 && team.status === 'free'" class="text-center py-5">
        <div class="text-muted">
          <font-awesome-icon icon="info-circle" class="h1 mb-3" />
          <p>Derzeit sind keine Upgrade-Optionen verfügbar.</p>
          <p class="small">Bitte wende dich an den Support für weitere Informationen.</p>
        </div>
      </div>

      <!-- Team Billing History -->
      <div>
        <TeamBillingHistory :team-id="teamId" :team="team" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { pricingApi } from '@/api/pricing'
import { teamService } from '@/api/teams'
import {
  showSuccess,
  showError,
  showConfirmDialog,
} from '@/services/notification'
import BackButton from '@/components/common/BackButton.vue'
import TeamBillingHistory from '@/components/teams/TeamBillingHistory.vue'

const route = useRoute()
const router = useRouter()
const teamsStore = useTeamsStore()

const { selectedTeam } = storeToRefs(teamsStore)

const loading = ref(true)
const error = ref(null)
const availablePlans = ref([])
const couponCode = ref('')
const couponLoading = ref(false)
const couponResult = ref(null)
const coinPackages = ref([])

const teamId = computed(() => route.params.id)
const team = computed(() => selectedTeam.value)

const canUpgrade = computed(() => {
  return team.value?.status === 'free'
})

const getStandardPackages = () => [
  { coins: 10, price: 1.42, pricePerCoin: 0.142, rule: null },
  { coins: 100, price: 14.16, pricePerCoin: 0.142, rule: null },
  { coins: 1000, price: 141.6, pricePerCoin: 0.142, rule: null },
]

const loadPricingRules = async () => {
  try {
    const response = await pricingApi.getCoinPricingRules()

    const rules = Array.isArray(response) ? response : response.data || response.rules || []

    if (!Array.isArray(rules)) {
      console.error('Invalid pricing rules response:', response)
      showError('Ungültige Antwort von der Preisregel-API')
      coinPackages.value = []
      return
    }

    const standardCoinAmounts = [10, 100, 1000]

    if (rules.length > 0) {
      coinPackages.value = standardCoinAmounts.map((coins) => {
        const matchingRule = rules.find((rule) => {
          const minCoins = rule.minCoins || 0
          const maxCoins = rule.maxCoins || Infinity
          return coins >= minCoins && coins <= maxCoins
        })

        if (matchingRule) {
          const pricePerCoin = matchingRule.pricePerCoin
          let bruttoPricePerCoin
          let price

          if (matchingRule.priceType === 'netto') {
            const vatAmount = pricePerCoin * 0.19
            bruttoPricePerCoin = pricePerCoin + vatAmount
            price = coins * bruttoPricePerCoin
          } else {
            bruttoPricePerCoin = pricePerCoin
            price = coins * pricePerCoin
          }

          return {
            coins: coins,
            price: Math.round(price * 100) / 100,
            pricePerCoin: Math.round(bruttoPricePerCoin * 1000) / 1000,
            rule: matchingRule,
          }
        } else {
          const bruttoPricePerCoin = 0.142
          const totalPrice = coins * bruttoPricePerCoin

          return {
            coins: coins,
            price: Math.round(totalPrice * 100) / 100,
            pricePerCoin: bruttoPricePerCoin,
            rule: null,
          }
        }
      })
    } else {
      coinPackages.value = getStandardPackages()
    }
  } catch (error) {
    console.error('Error loading pricing rules:', error)
    showError('Fehler beim Laden der Preisregeln')
    coinPackages.value = []
  }
}

const loadTeamData = async () => {
  try {
    await teamsStore.loadTeam(teamId.value)
  } catch (error) {
    console.error('Error loading team data:', error)
  }
}

const getPlanDisplayName = (status) => {
  switch (status) {
    case 'free':
      return 'Kostenloser Plan'
    case 'pro':
      return 'Pro Plan'
    case 'ultimate':
      return 'Ultimate Plan'
    default:
      return 'Unbekannter Plan'
  }
}

const getPlanDescription = (status) => {
  switch (status) {
    case 'free':
      return 'Grundlegende Funktionen'
    case 'pro':
      return 'Erweiterte Funktionen'
    case 'ultimate':
      return 'Alle Funktionen'
    default:
      return ''
  }
}

const getPlanBadgeClass = (status) => {
  switch (status) {
    case 'free':
      return 'bg-secondary'
    case 'pro':
      return 'bg-primary'
    case 'ultimate':
      return 'bg-warning text-dark'
    default:
      return 'bg-secondary'
  }
}

const getPlanCardClass = (planType) => {
  if (team.value?.status === planType) {
    return 'border-primary'
  }
  return ''
}

const formatCurrency = (price) => {
  if (!price) return '€0,00'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(price)
}

const upgradeToPlan = async (plan) => {
  try {
    if (!canUpgrade.value) {
      showError('Upgrade ist nicht verfügbar. Du hast bereits einen aktiven Plan.')
      return
    }

    if (team.value?.status === plan.type) {
      showError('Dieser Plan ist bereits aktiv.')
      return
    }

    // Navigate to SalesView with pre-selected team and plan
    router.push({
      name: 'sales',
      query: {
        teamId: teamId.value,
        planType: plan.type,
        couponCode: couponCode.value || undefined
      }
    })
  } catch (error) {
    console.error('Fehler beim Upgrade:', error)
    showError('Fehler beim Upgrade. Bitte versuche es erneut.')
  }
}

const handleTeamUpgrade = async (planType, billingData, couponCode, paymentMethod = 'paypal') => {
  try {
    const result = await teamService.upgradeTeam(
      teamId.value,
      planType,
      billingData,
      couponCode,
      paymentMethod,
    )

    if (result.requiresPayment && result.paypalOrder) {
      
      showSuccess('PayPal-Zahlung wird vorbereitet...')

      
      window.open(result.paypalOrder.approvalUrl || result.paypalOrder.redirectUrl, '_self', '')

      
      setTimeout(async () => {
        try {
          await teamsStore.loadTeamById(teamId.value)
        } catch (error) {
          console.error('Error updating after PayPal payment:', error)
        }
      }, 5000)
    } else if (couponCode) {
      
      showSuccess(
        `Bereich erfolgreich auf ${planType === 'team_pro' ? 'Pro' : 'Ultimate'} Plan upgegradet! Das kostenlose Upgrade wurde erfolgreich angewendet.`,
      )
      resetCouponFields()

      await teamsStore.loadTeamById(teamId.value)
      router.push({ name: 'teams-detail', params: { id: teamId.value } })
    } else if (paymentMethod === 'bank_transfer') {
      
      showSuccess(
        `Bereich erfolgreich auf ${planType === 'team_pro' ? 'Pro' : 'Ultimate'} Plan upgegradet! Die Zahlungsaufforderung wurde per E-Mail versendet.`,
      )

      await teamsStore.loadTeamById(teamId.value)
      router.push({ name: 'teams-detail', params: { id: teamId.value } })
    } else {
      
      showSuccess(
        `Bereich erfolgreich auf ${planType === 'team_pro' ? 'Pro' : 'Ultimate'} Plan upgegradet!`,
      )

      await teamsStore.loadTeamById(teamId.value)
      router.push({ name: 'teams-detail', params: { id: teamId.value } })
    }
  } catch (error) {
    console.error('Upgrade error:', error)

    let errorMessage = 'Fehler beim Team-Upgrade'

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }

    showError(errorMessage)
  }
}

const resetCouponFields = () => {
  couponCode.value = ''
  couponResult.value = null
}

const showCouponCoinPurchaseModalForTeam = async (preValidatedCoupon = null, initialCode = '') => {
  try {
    // Navigate to purchase page with coupon
    const normalizedInitialCode = initialCode ? initialCode.trim().toUpperCase() : ''
    const couponCode = normalizedInitialCode || preValidatedCoupon?.coupon?.code || ''
    
    router.push({
      path: '/purchase',
      query: {
        couponCode: couponCode,
        teamId: teamId.value
      }
    })
  } catch (error) {
    console.error('Error navigating to purchase page:', error)
    showError('Fehler beim Öffnen der Kaufseite')
  }
}

const handleTeamCoinPurchase = async (purchaseData) => {
  try {
    
    
    const { purchaseCoins } = await import('@/api/billing')
    const result = await purchaseCoins(
      purchaseData.coins,
      purchaseData.billingData,
      purchaseData.couponCode,
      purchaseData.paymentMethod || 'paypal',
    )

    const method = result?.billing?.method
    const payed = result?.billing?.payed
    const documentLabel = method === 'bank_transfer' && !payed ? 'Zahlungsaufforderung' : 'Rechnung'
    showSuccess(
      result.message ||
        `${result?.billing?.coins || ''} Quittungen erfolgreich gekauft! Die ${documentLabel} wurde per E-Mail versendet.`,
    )

    
    resetCouponFields()

    
    await loadTeamData()
  } catch (error) {
    console.error('Team coin purchase error:', error)
    showError(error.response?.data?.message || 'Fehler beim Kauf der Quittungen')
  }
}

const loadTeamPlanFeatures = async () => {
  try {
    const plans = await pricingApi.getTeamPlanFeatures()
    availablePlans.value =
      team.value?.status !== 'free'
        ? plans.filter((plan) => plan.type !== `team_${team.value?.status}`)
        : plans || []
  } catch (error) {
    console.warn('Team-Plan Features konnten nicht geladen werden:', error)
    availablePlans.value = []
  }
}

const validateCoupon = async () => {
  if (!couponCode.value.trim()) return

  couponLoading.value = true
  couponResult.value = null

  try {
    const { couponsApi } = await import('@/api/coupons')
    const result = await couponsApi.validateCoupon(
      couponCode.value.trim().toUpperCase(),
      'team_upgrade',
      0,
    )

    if (result.coupon.type === 'free_coins' || result.coupon.type === 'user_related') {
      await showCouponCoinPurchaseModalForTeam(result, couponCode.value.trim().toUpperCase())
      return
    }

    couponResult.value = {
      success: true,
      data: result,
    }
  } catch (error) {
    couponResult.value = {
      success: false,
      error: error.response?.data?.message || 'Gutschein konnte nicht validiert werden',
    }
  } finally {
    couponLoading.value = false
  }
}

const getCouponDescription = (coupon) => {
  if (coupon.type === 'free_team_pro') {
    const planText = coupon.teamProPlan === 'ultimate' ? 'Ultimate' : 'Pro'
    const durationUnit = coupon.teamProDurationUnit || 'months'
    const durationLabels = {
      days: 'Tage',
      months: 'Monate',
      years: 'Jahre',
    }
    const durationLabel = durationLabels[durationUnit] || durationUnit
    const durationText =
      coupon.teamProDuration === 1
        ? `1 ${durationLabel.slice(0, -1)}`
        : `${coupon.teamProDuration} ${durationLabel}`
    const coinsText = coupon.teamProCoins > 0 ? ` + ${coupon.teamProCoins} Quittungen (Gesamt)` : ''
    return `Kostenloses Team ${planText} für ${durationText}${coinsText}`
  }
  return coupon.description || 'Gutschein'
}

const hasCouponConditions = (coupon) => {
  return coupon.minPurchaseAmount || coupon.maxDiscountAmount
}

const getCouponConditions = (coupon) => {
  const conditions = []

  if (coupon.minPurchaseAmount) {
    conditions.push(`Mindestbestellwert: ${formatCurrency(coupon.minPurchaseAmount)}`)
  }

  if (coupon.maxDiscountAmount) {
    conditions.push(`Max. Rabatt: ${formatCurrency(coupon.maxDiscountAmount)}`)
  }

  return conditions.join(' • ')
}

const applyCoupon = async (couponData) => {
  try {
    // Navigate to SalesView with pre-selected team, plan and coupon
    const planType = couponData.coupon.teamProPlan === 'ultimate' ? 'team_ultimate' : 'team_pro'
    
    router.push({
      name: 'sales',
      query: {
        teamId: teamId.value,
        planType: planType,
        couponCode: couponCode.value.trim().toUpperCase()
      }
    })
  } catch (error) {
    console.error('Fehler beim Anwenden des Coupons:', error)
    showError('Fehler beim Anwenden des Gutscheins')

    resetCouponFields()
  }
}

const getDurationUnitLabel = (unit) => {
  const labels = {
    days: 'Tage',
    months: 'Monate',
    years: 'Jahre',
  }
  return labels[unit] || unit
}

onMounted(async () => {
  const teamId = route.params.id
  if (teamId) {
    loading.value = true
    try {
      const team = await teamsStore.loadTeamById(teamId)
      if (!team) {
        router.push({ name: 'teams-list' })
        return
      }

      await loadTeamPlanFeatures()
    } catch (err) {
      error.value = 'Team konnte nicht geladen werden'
    } finally {
      loading.value = false
    }
  }
})
</script>
